import { Children, cloneElement, useState } from "react"

import { handleClickForKeyboard } from "simplydo/core/accessibility"
import { styled } from "styled-components"

const RadioCardContent = styled.div<{
  $selected: boolean
  $editable: boolean
}>`
  cursor: ${(props) => (props.$editable ? `pointer` : "default")};
  padding: 16px;
  border-radius: 4px;
  box-shadow:
    0 0 0 1px rgba(34, 36, 38, 0.15) inset,
    0 2px 4px 0 rgba(34, 36, 38, 0.12),
    0 2px 10px 0 rgba(34, 36, 38, 0.15);

  outline: ${(props) => (props.$selected ? `2px solid #2185d0` : "none")};
  opacity: ${(props) => (props.$editable ? 1 : 0.5)};

  &:focus {
    outline: ${(props) => (props.$selected ? `2px solid #2185d0` : props.$editable ? `1px solid #2185d0` : "none")};
  }
  &:hover {
    outline: ${(props) => (props.$selected ? `2px solid #2185d0` : props.$editable ? `1px solid #2185d0` : "none")};
  }
`

const RadioCardContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`

type RadioCardItemProps = {
  children: React.ReactNode
  value: string
  selected?: boolean
  editable?: boolean
  onClick?: () => void
}

const RadioCardItem = ({ children, selected, editable, onClick }: RadioCardItemProps) => {
  return (
    <RadioCardContent
      role="radio"
      aria-checked={selected}
      tabIndex={0}
      $editable={editable}
      $selected={selected}
      onClick={onClick}
      onKeyDown={handleClickForKeyboard(onClick)}
    >
      {children}
    </RadioCardContent>
  )
}

type RadioCardGroupProps = {
  children: React.ReactNode
  defaultValue: string
  onValueChange?: (value: string) => void
  editable?: boolean
}

const RadioCardGroup = ({ children, defaultValue, onValueChange, editable }: RadioCardGroupProps) => {
  const [selectedValue, setSelectedValue] = useState(defaultValue)

  return (
    <RadioCardContainer role="radiogroup">
      {Children.map(children, (child: React.ReactElement) => {
        if (!child) {
          return null
        }
        // @ts-ignore
        if (child.type.name && child.type.name !== RadioCardItem.name) {
          return child
        }
        return cloneElement(child, {
          selected: child.props.selected ?? selectedValue === child.props.value,
          editable: editable,
          onClick: () => {
            if (!editable) {
              return
            }
            setSelectedValue(child.props.value)
            onValueChange?.(child.props.value)
          },
        })
      })}
    </RadioCardContainer>
  )
}

export const RadioCard = {
  Group: RadioCardGroup,
  Item: RadioCardItem,
}
